import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  ChallengeParameters
} from 'amazon-cognito-identity-js';
import { jwtDecode } from 'jwt-decode';
import MobileAuthService from '../services/MobileAuthService';

// Updated pool data with new User Pool ID and Web Client ID
const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID // Web client for browser-based auth
};

const userPool = new CognitoUserPool(poolData);
const msg91Auth = new MobileAuthService();

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newPasswordChallenge, setNewPasswordChallenge] = useState(null); // { cognitoUser, userAttributes }
  const [otpChallenge, setOtpChallenge] = useState(null); // { cognitoUser, phone, challengeName }
  const [pendingUser, setPendingUser] = useState(null); // For OTP verification
  const [msg91Challenge, setMSG91Challenge] = useState(null); // MSG91 OTP challenge state
  const [registrationData, setRegistrationData] = useState(null); // For new user registration
  
  // Add debouncing to prevent race conditions
  const [authCheckInProgress, setAuthCheckInProgress] = useState(false);
  const [lastAuthCheck, setLastAuthCheck] = useState(0);
  const [authCheckTimeout, setAuthCheckTimeout] = useState(null);
  
  const navigate = useNavigate();

  // Navigate user based on their role
  const navigateBasedOnRole = (role) => {
    console.log('Navigating user based on role:', role);
    
    switch (role) {
      case 'super_admin':
        navigate('/super-admin-dashboard');
        break;
      case 'building_admin':
        navigate('/building-admin-dashboard');
        break;
      case 'manager':
        navigate('/manager-dashboard');
        break;
      case 'security':
        navigate('/security-dashboard');
        break;
      case 'maintenance_staff':
        navigate('/staff-dashboard');
        break;
      case 'resident':
      default:
        navigate('/resident-dashboard');
        break;
    }
  };

  useEffect(() => {
    checkAuthState();
    
    // Cleanup function to clear any pending auth check timeout
    return () => {
      if (authCheckTimeout) {
        clearTimeout(authCheckTimeout);
        setAuthCheckTimeout(null);
      }
    };
  }, []);

  // Cleanup effect for timeouts
  useEffect(() => {
    return () => {
      if (authCheckTimeout) {
        clearTimeout(authCheckTimeout);
      }
    };
  }, [authCheckTimeout]);

  // Helper function to refresh session using refresh token
  const refreshSession = (cognitoUser) => {
    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          console.error('Error getting session:', err);
          reject(err);
          return;
        }
        
        // Check if session is valid
        if (session && session.isValid()) {
          console.log('Session is valid, no refresh needed');
          resolve(session);
          return;
        }

        console.log('Session invalid, attempting refresh...');
        
        // Try to refresh the session using refresh token
        if (session) {
          const refreshToken = session.getRefreshToken();
          if (!refreshToken) {
            console.error('No refresh token available in session');
            reject(new Error('No refresh token available'));
            return;
          }

          cognitoUser.refreshSession(refreshToken, (refreshErr, refreshedSession) => {
            if (refreshErr) {
              console.error('Token refresh failed:', refreshErr);
              reject(refreshErr);
            } else {
              console.log('Session refreshed successfully');
              resolve(refreshedSession);
            }
          });
        } else {
          console.error('Session object is null');
          reject(new Error('Session object is null'));
        }
      });
    });
  };

  // Helper function to check if token is about to expire (within 5 minutes)
  const isTokenExpiringSoon = (session) => {
    try {
      const idToken = session.getIdToken();
      const expirationTime = idToken.getExpiration(); // Unix timestamp in seconds
      const currentTime = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = expirationTime - currentTime;
      
      // Return true if token expires in less than 5 minutes (300 seconds)
      return timeUntilExpiry < 300;
    } catch (error) {
      console.error('Error checking token expiry:', error);
      return true; // Assume expiring if we can't check
    }
  };

  // Helper function to load user from localStorage as fallback
  const loadUserFromLocalStorage = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        console.log('Loaded user from localStorage as fallback:', parsedUser.username);
        return parsedUser;
      }
    } catch (error) {
      console.error('Failed to load user from localStorage:', error);
    }
    return null;
  };

  // Helper function to reconstruct CognitoUser from username if session exists
  const reconstructCognitoUser = (username) => {
    try {
      if (!username) return null;
      const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
      console.log('Reconstructed CognitoUser from username:', username);
      return cognitoUser;
    } catch (error) {
      console.error('Failed to reconstruct CognitoUser:', error);
      return null;
    }
  };

  // Helper function to set user data from session
  const setUserDataFromSession = (cognitoUser, session) => {
    cognitoUser.getUserAttributes((err, attributes) => {
      if (err) {
        console.error('Error fetching user attributes:', err);
        // Try to use localStorage fallback
        const fallbackUser = loadUserFromLocalStorage();
        if (fallbackUser) {
          setUser(fallbackUser);
          console.log('Using localStorage fallback user data');
        } else {
          setUser(null);
          try {
            localStorage.removeItem('user');
          } catch (e) {
            console.error('Failed to clear localStorage:', e);
          }
        }
        setLoading(false);
      } else {
        // Extract groups from ID token
        let groups = [];
        try {
          const idToken = session.getIdToken().getJwtToken();
          const decoded = jwtDecode(idToken);
          groups = decoded['cognito:groups'] || [];
        } catch (e) {
          console.error('Error decoding token:', e);
          groups = [];
        }
        
        const userData = {
          username: cognitoUser.getUsername(),
          attributes: Object.fromEntries(attributes.map(attr => [attr.getName(), attr.getValue()])),
          groups,
        };
        
        setUser(userData);
        try {
          localStorage.setItem('user', JSON.stringify(userData));
          console.log('Auth state checked successfully, user:', userData.username);
        } catch (e) {
          console.error('Failed to save user to localStorage:', e);
        }
        setLoading(false);
      }
    });
  };

  const checkAuthState = async () => {
    const now = Date.now();
    
    // Prevent concurrent auth checks
    if (authCheckInProgress) {
      console.log('[AuthContext] Auth check already in progress, skipping');
      return;
    }
    
    // Debounce rapid successive calls (within 1 second)
    if (now - lastAuthCheck < 1000) {
      console.log('[AuthContext] Auth check debounced');
      // Clear existing timeout and set a new one
      if (authCheckTimeout) {
        clearTimeout(authCheckTimeout);
      }
      const timeout = setTimeout(() => {
        checkAuthState();
      }, 1000);
      setAuthCheckTimeout(timeout);
      return;
    }
    
    setAuthCheckInProgress(true);
    setLastAuthCheck(now);
    
    try {
      let cognitoUser = userPool.getCurrentUser();
      console.log("Checking auth state, cognitoUser:", cognitoUser ? cognitoUser.getUsername() : 'null');
      
      // If getCurrentUser returns null, try to reconstruct from stored username
      if (!cognitoUser) {
        const fallbackUser = loadUserFromLocalStorage();
        if (fallbackUser && fallbackUser.username) {
          console.log('getCurrentUser returned null, attempting to reconstruct CognitoUser...');
          cognitoUser = reconstructCognitoUser(fallbackUser.username);
          
          if (cognitoUser) {
            console.log('Successfully reconstructed CognitoUser, will try to get session...');
          } else {
            console.log('Failed to reconstruct CognitoUser');
            setUser(fallbackUser);
            setLoading(false);
            setAuthCheckInProgress(false);
            return;
          }
        } else {
          // No Cognito user and no localStorage fallback - truly logged out
          console.log('No Cognito user and no localStorage fallback - setting user to null');
          setUser(null);
          setLoading(false);
          setAuthCheckInProgress(false);
          return;
        }
      }

      try {
        // Attempt to get or refresh the session
        const session = await refreshSession(cognitoUser);
        console.log("Session valid or refreshed successfully");
        setUserDataFromSession(cognitoUser, session);
      } catch (sessionError) {
        console.error('Session validation/refresh failed:', sessionError);
        
        // Session couldn't be refreshed, try localStorage fallback
        const fallbackUser = loadUserFromLocalStorage();
        if (fallbackUser) {
          console.log('Session expired but using localStorage fallback temporarily');
          setUser(fallbackUser);
          setLoading(false);
          
          // Optionally: You could prompt user to re-authenticate here
          // For now, the user will be logged out on next API call that requires fresh token
        } else {
          // No fallback available, clear everything
          setUser(null);
          try {
            localStorage.removeItem('user');
          } catch (e) {
            console.error('Failed to clear localStorage:', e);
          }
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Unexpected error in checkAuthState:', error);
      setUser(null);
      setLoading(false);
    } finally {
      setAuthCheckInProgress(false);
    }
  };

  const login = (email, password) => {
    return new Promise((resolve) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      const authDetails = new AuthenticationDetails({ Username: email, Password: password });
      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (result) => {
          cognitoUser.getUserAttributes((err, attributes) => {
            if (err) {
              setUser(null);
              try {
                localStorage.removeItem('user');
              } catch (e) {
                console.error('Failed to clear localStorage:', e);
              }
              resolve({ success: false, error: err.message });
            } else {
              // Extract groups from ID token
              let groups = [];
              try {
                const idToken = result.getIdToken().getJwtToken();
                const decoded = jwtDecode(idToken);
                groups = decoded['cognito:groups'] || [];
              } catch (e) {
                console.error('Error decoding token:', e);
                groups = [];
              }
              const userData = {
                username: cognitoUser.getUsername(),
                attributes: Object.fromEntries(attributes.map(attr => [attr.getName(), attr.getValue()])),
                groups,
              };
              setUser(userData);
              try {
                localStorage.setItem('user', JSON.stringify(userData));
              } catch (e) {
                console.error('Failed to save user to localStorage:', e);
              }
              setNewPasswordChallenge(null);
              navigate('/');
              resolve({ success: true });
            }
          });
        },
        onFailure: (err) => {
          setUser(null);
          try {
            localStorage.removeItem('user');
          } catch (e) {
            console.error('Failed to clear localStorage:', e);
          }
          setNewPasswordChallenge(null);
          let errorMessage = 'Login failed';
          if (err.code === 'UserNotConfirmedException') {
            errorMessage = 'Please confirm your email address before signing in.';
          } else if (err.code === 'NotAuthorizedException') {
            errorMessage = 'Invalid email or password.';
          } else if (err.code === 'UserNotFoundException') {
            errorMessage = 'User not found. Please check your email address.';
          } else if (err.code === 'TooManyRequestsException') {
            errorMessage = 'Too many failed attempts. Please try again later.';
          } else if (err.message) {
            errorMessage = err.message;
          }
          resolve({ success: false, error: errorMessage });
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          setNewPasswordChallenge({ cognitoUser, userAttributes });
          resolve({ success: false, error: 'NEW_PASSWORD_REQUIRED' });
        }
      });
    });
  };

  // Handler to complete new password challenge
  const completeNewPassword = (newPassword, attributes = {}) => {
    return new Promise((resolve) => {
      if (!newPasswordChallenge) {
        resolve({ success: false, error: 'No new password challenge in progress.' });
        return;
      }
      const { cognitoUser } = newPasswordChallenge;
      // Filter out non-mutable attributes
      const NON_MUTABLE_ATTRS = ['email_verified', 'phone_number_verified'];
      const filteredAttributes = Object.fromEntries(
        Object.entries(attributes).filter(([key]) => !NON_MUTABLE_ATTRS.includes(key))
      );
      cognitoUser.completeNewPasswordChallenge(newPassword, filteredAttributes, {
        onSuccess: (result) => {
          cognitoUser.getUserAttributes((err, userAttrs) => {
            if (err) {
              setUser(null);
              try {
                localStorage.removeItem('user');
              } catch (e) {
                console.error('Failed to clear localStorage:', e);
              }
              setNewPasswordChallenge(null);
              resolve({ success: false, error: err.message });
            } else {
              const userData = {
                username: cognitoUser.getUsername(),
                attributes: Object.fromEntries(userAttrs.map(attr => [attr.getName(), attr.getValue()]))
              };
              setUser(userData);
              try {
                localStorage.setItem('user', JSON.stringify(userData));
              } catch (e) {
                console.error('Failed to save user to localStorage:', e);
              }
              setNewPasswordChallenge(null);
              navigate('/');
              resolve({ success: true });
            }
          });
        },
        onFailure: (err) => {
          setUser(null);
          try {
            localStorage.removeItem('user');
          } catch (e) {
            console.error('Failed to clear localStorage:', e);
          }
          setNewPasswordChallenge(null);
          let errorMessage = 'Failed to set new password.';
          if (err.message) errorMessage = err.message;
          resolve({ success: false, error: errorMessage });
        }
      });
    });
  };

  const logout = () => {
    try {
      const cognitoUser = userPool.getCurrentUser();
      if (cognitoUser) {
        cognitoUser.signOut();
      }
    } catch (error) {
      console.error('Error during Cognito sign out:', error);
    }
    
    setUser(null);
    
    // Clear all auth-related localStorage items with error handling
    try {
      localStorage.removeItem('user');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('tokenExpiry');
    } catch (error) {
      console.error('Failed to clear localStorage during logout:', error);
    }
    
    setNewPasswordChallenge(null);
    setOtpChallenge(null);
    setPendingUser(null);
    setMSG91Challenge(null);
    setRegistrationData(null);
    
    navigate('/login');
  };

  const getAuthToken = async () => {
    try {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) {
        throw new Error('No user');
      }

      // Get the current session first
      const currentSession = await new Promise((resolve, reject) => {
        cognitoUser.getSession((err, session) => {
          if (err) reject(err);
          else resolve(session);
        });
      });

      // Check if token is expiring soon and proactively refresh
      if (isTokenExpiringSoon(currentSession)) {
        console.log('Token expiring soon, refreshing proactively...');
        const refreshedSession = await refreshSession(cognitoUser);
        return refreshedSession.getIdToken().getJwtToken();
      }

      // Use the refresh session helper to get or refresh the token
      const session = await refreshSession(cognitoUser);
      const idToken = session.getIdToken().getJwtToken();
      return idToken;
    } catch (error) {
      console.error('Failed to get auth token:', error);
      throw new Error('No valid session');
    }
  };

  // Function to send OTP using MSG91
  const sendMSG91OTP = async (phoneNumber) => {
    try {
      const result = await msg91Auth.sendOTP(phoneNumber);
      
      if (result.success) {
        setMSG91Challenge({
          phoneNumber,
          timestamp: Date.now(),
          userExists: result.data.userExists
        });
        return { success: true, userExists: result.data.userExists };
      } else {
        return { success: false, error: result.error, message: result.message };
      }
    } catch (error) {
      console.error('MSG91 Send OTP failed:', error);
      return { success: false, error: 'Network error', message: error.message };
    }
  };

  // Function to verify MSG91 OTP
  const verifyMSG91OTP = async (phoneNumber, otp) => {
    try {
      const result = await msg91Auth.verifyOTP(phoneNumber, otp);
      
      if (result.success) {
        const data = result.data;
        
        if (data.tokens) {
          // User authenticated successfully with tokens
          const userData = {
            username: data.user.username,
            attributes: data.user.attributes,
            role: data.user.role,
            groups: data.user.groups || [],
            phoneNumber: data.phoneNumber,
            userStatus: data.user.userStatus,
            phoneNumberVerified: data.phoneNumberVerified
          };
          
          // Store tokens
          localStorage.setItem('accessToken', data.tokens.accessToken);
          localStorage.setItem('idToken', data.tokens.idToken);
          localStorage.setItem('refreshToken', data.tokens.refreshToken);
          localStorage.setItem('tokenExpiry', (Date.now() + data.tokens.expiresIn * 1000).toString());
          
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          setMSG91Challenge(null);
          
          // Navigate based on user role
          navigateBasedOnRole(data.user.role);
          
          return { success: true, authenticated: true, user: userData, tokens: data.tokens };
          
        } else if (data.requiresPasswordUpdate) {
          // User needs to set password
          setMSG91Challenge({
            ...msg91Challenge,
            requiresPassword: true,
            session: data.session,
            userData: data.user
          });
          return { success: true, requiresPassword: true, session: data.session };
          
        } else if (data.requiresRegistration) {
          // New user needs registration
          setRegistrationData({ phoneNumber, otpVerified: true });
          setMSG91Challenge(null);
          return { success: true, requiresRegistration: true };
          
        } else {
          // Other cases
          return { success: true, requiresManualAuth: true, userData: data.user };
        }
      } else {
        return { 
          success: false, 
          error: result.error, 
          message: result.message,
          attemptsRemaining: result.attemptsRemaining 
        };
      }
    } catch (error) {
      console.error('MSG91 Verify OTP failed:', error);
      return { success: false, error: 'Network error', message: error.message };
    }
  };

  // Function to complete password challenge for MSG91
  const completeMSG91Password = async (phoneNumber, newPassword, session) => {
    try {
      const result = await msg91Auth.completePasswordChallenge(phoneNumber, newPassword, session);
      
      if (result.success && result.data.tokens) {
        const userData = {
          username: result.data.user.username,
          attributes: result.data.user.attributes,
          groups: [], // Extract from token if needed
        };
        
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        setMSG91Challenge(null);
        navigate('/');
        return { success: true };
      } else {
        return { success: false, error: result.error, message: result.message };
      }
    } catch (error) {
      console.error('MSG91 Complete Password failed:', error);
      return { success: false, error: 'Network error', message: error.message };
    }
  };

  // Function to register new user via MSG91
  const registerMSG91User = async (phoneNumber, firstName, lastName, email = null, password = null) => {
    try {
      const result = await msg91Auth.registerUser(phoneNumber, firstName, lastName, email, password);
      
      if (result.success) {
        // After registration, initiate login process
        setRegistrationData(null);
        return { success: true, message: 'Registration successful' };
      } else {
        return { success: false, error: result.error, message: result.message };
      }
    } catch (error) {
      console.error('MSG91 Registration failed:', error);
      return { success: false, error: 'Network error', message: error.message };
    }
  };

  // Function to resend MSG91 OTP
  const resendMSG91OTP = async (phoneNumber) => {
    try {
      const result = await msg91Auth.resendOTP(phoneNumber);
      
      if (result.success) {
        setMSG91Challenge({
          ...msg91Challenge,
          timestamp: Date.now()
        });
        return { success: true };
      } else {
        return { success: false, error: result.error, message: result.message };
      }
    } catch (error) {
      console.error('MSG91 Resend OTP failed:', error);
      return { success: false, error: 'Network error', message: error.message };
    }
  };

  // Function to initiate phone number authentication (OTP) - Legacy Cognito
  const initiatePhoneAuth = (phoneNumber) => {
    return new Promise(async (resolve) => {
      try {
        console.log('Initiating phone auth for:', phoneNumber);
        
        // Call backend API to initiate OTP
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://myhomeentry.com/api'}/initiate-otp`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ phoneNumber })
        });

        const result = await response.json();

        if (response.ok && result.success) {
          // Set up OTP challenge state
          setOtpChallenge({ 
            phone: phoneNumber, 
            challengeName: 'SMS_OTP',
            timestamp: Date.now(),
            debugOtp: result.debug_otp // For demo purposes
          });
          
          resolve({ success: true, challengeRequired: true });
        } else {
          resolve({ success: false, error: result.error || 'Failed to send OTP' });
        }
        
      } catch (err) {
        console.error('Phone auth initiation failed:', err);
        // Fallback for demo when backend isn't available yet
        setOtpChallenge({ 
          phone: phoneNumber, 
          challengeName: 'SMS_OTP',
          timestamp: Date.now(),
          debugOtp: '123456' // Demo OTP
        });
        resolve({ success: true, challengeRequired: true });
      }
    });
  };

  // Function to verify OTP code
  const verifyOTP = (otpCode) => {
    return new Promise(async (resolve) => {
      if (!otpChallenge) {
        resolve({ success: false, error: 'No OTP challenge in progress' });
        return;
      }

      try {
        console.log('Verifying OTP:', otpCode, 'for phone:', otpChallenge.phone);
        
        // Call backend API to verify OTP
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://myhomeentry.com/api'}/verify-otp`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            phoneNumber: otpChallenge.phone,
            otp: otpCode 
          })
        });

        const result = await response.json();

        if (response.ok && result.success) {
          // Set user data from backend response
          const userData = {
            username: result.user.username,
            attributes: result.user.attributes,
            groups: result.user.groups,
          };
          
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          setOtpChallenge(null);
          navigate('/');
          resolve({ success: true });
        } else {
          resolve({ success: false, error: result.error || 'Invalid OTP code' });
        }
      } catch (err) {
        console.error('OTP verification failed:', err);
        // Fallback for demo when backend isn't available yet
        if (otpCode === '123456' || otpCode === otpChallenge.debugOtp) {
          const mockUserData = {
            username: otpChallenge.phone,
            attributes: {
              phone_number: otpChallenge.phone,
              phone_number_verified: 'true',
              given_name: 'Phone',
              family_name: 'User'
            },
            groups: ['owner'],
          };
          
          setUser(mockUserData);
          localStorage.setItem('user', JSON.stringify(mockUserData));
          setOtpChallenge(null);
          navigate('/');
          resolve({ success: true });
        } else {
          resolve({ success: false, error: 'Network error. Please check your connection and try again.' });
        }
      }
    });
  };

  // Function to resend OTP
  const resendOTP = () => {
    if (!otpChallenge) {
      return Promise.resolve({ success: false, error: 'No OTP challenge in progress' });
    }
    return initiatePhoneAuth(otpChallenge.phone);
  };

  // Clear OTP challenge
  const clearOtpChallenge = () => {
    setOtpChallenge(null);
    setPendingUser(null);
  };

  // Forgot Password - Send reset code
  const forgotPassword = (username) => {
    return new Promise((resolve) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool
      });

      cognitoUser.forgotPassword({
        onSuccess: (result) => {
          console.log('Password reset code sent successfully:', result);
          resolve({
            success: true,
            codeDeliveryDetails: result.CodeDeliveryDetails
          });
        },
        onFailure: (err) => {
          console.error('Forgot password error:', err);
          let errorMessage = 'Failed to send reset code';
          
          if (err.code === 'UserNotFoundException') {
            errorMessage = 'User not found';
          } else if (err.code === 'InvalidParameterException') {
            errorMessage = 'Invalid email address';
          } else if (err.code === 'LimitExceededException') {
            errorMessage = 'Too many requests. Please try again later';
          } else if (err.message) {
            errorMessage = err.message;
          }
          
          resolve({ success: false, error: errorMessage });
        },
        inputVerificationCode: (data) => {
          console.log('Code input required:', data);
          resolve({
            success: true,
            codeDeliveryDetails: data.CodeDeliveryDetails
          });
        }
      });
    });
  };

  // Confirm Password Reset - Verify code and set new password
  const confirmPasswordReset = (username, verificationCode, newPassword) => {
    return new Promise((resolve) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool
      });

      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: () => {
          console.log('Password reset successful');
          resolve({ success: true });
        },
        onFailure: (err) => {
          console.error('Password reset confirmation error:', err);
          let errorMessage = 'Failed to reset password';
          
          if (err.code === 'CodeMismatchException') {
            errorMessage = 'Invalid verification code';
          } else if (err.code === 'InvalidPasswordException') {
            errorMessage = 'Password does not meet requirements';
          } else if (err.code === 'ExpiredCodeException') {
            errorMessage = 'Verification code has expired';
          } else if (err.message) {
            errorMessage = err.message;
          }
          
          resolve({ success: false, error: errorMessage });
        }
      });
    });
  };

  const value = {
    user,
    login,
    logout,
    loading,
    getAuthToken,
    newPasswordChallenge,
    completeNewPassword,
    otpChallenge,
    initiatePhoneAuth,
    verifyOTP,
    resendOTP,
    clearOtpChallenge,
    // Forgot Password Functions
    forgotPassword,
    confirmPasswordReset,
    // MSG91 Functions
    sendMSG91OTP,
    verifyMSG91OTP,
    completeMSG91Password,
    registerMSG91User,
    resendMSG91OTP,
    msg91Challenge,
    registrationData,
    setMSG91Challenge: (challenge) => setMSG91Challenge(challenge),
    setRegistrationData: (data) => setRegistrationData(data)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 